import os from 'os'
import { execSync } from 'child_process'
import type { CommandHandlerFn } from '../command-handler'

export function createSystemHandlers(): Record<string, CommandHandlerFn> {
  return {
    'system:info': systemInfo,
    'system:health': systemHealth,
    'system:heartbeat': systemHeartbeat,
    'disk:usage': diskUsage,
    'network:info': networkInfo,
  }
}

async function systemInfo() {
  return {
    nodeVersion: process.version,
    platform: os.platform(),
    arch: os.arch(),
    hostname: os.hostname(),
    uptime: os.uptime(),
    memory: {
      total: os.totalmem(),
      free: os.freemem(),
      used: os.totalmem() - os.freemem(),
    },
    cpus: os.cpus().map((cpu) => ({
      model: cpu.model,
      speed: cpu.speed,
    })),
    pid: process.pid,
  }
}

async function systemHealth() {
  const memUsage = process.memoryUsage()
  return {
    status: 'healthy',
    uptime: process.uptime(),
    memoryUsage: {
      rss: memUsage.rss,
      heapTotal: memUsage.heapTotal,
      heapUsed: memUsage.heapUsed,
      external: memUsage.external,
    },
    loadAvg: os.loadavg(),
  }
}

async function systemHeartbeat() {
  const mem = process.memoryUsage()
  const cpus = os.cpus()
  const cpuTotal = cpus.reduce((acc, cpu) => {
    const times = cpu.times
    return acc + times.user + times.nice + times.sys + times.idle + times.irq
  }, 0)
  const cpuIdle = cpus.reduce((acc, cpu) => acc + cpu.times.idle, 0)
  const cpuUsage = cpuTotal > 0 ? Math.round(((cpuTotal - cpuIdle) / cpuTotal) * 100) : 0

  return {
    timestamp: Date.now(),
    cpu: { usage: cpuUsage, cores: cpus.length },
    memory: {
      total: os.totalmem(),
      free: os.freemem(),
      usage: Math.round(((os.totalmem() - os.freemem()) / os.totalmem()) * 100),
      processRss: mem.rss,
      processHeap: mem.heapUsed,
    },
    uptime: { system: os.uptime(), process: process.uptime() },
    loadAvg: os.loadavg(),
  }
}

async function diskUsage() {
  const platform = os.platform()
  try {
    if (platform === 'win32') {
      const output = execSync('wmic logicaldisk get size,freespace,caption', { encoding: 'utf-8' })
      return parseDiskWindows(output)
    } else {
      const output = execSync('df -P', { encoding: 'utf-8' })
      return parseDiskUnix(output)
    }
  } catch {
    return []
  }
}

function parseDiskUnix(output: string) {
  const lines = output.trim().split('\n').slice(1)
  return lines.map((line) => {
    const parts = line.split(/\s+/)
    const total = parseInt(parts[1]) * 1024
    const used = parseInt(parts[2]) * 1024
    const available = parseInt(parts[3]) * 1024
    return {
      filesystem: parts[0],
      mount: parts[5],
      total,
      used,
      available,
      usagePercent: total > 0 ? Math.round((used / total) * 100) : 0,
    }
  })
}

function parseDiskWindows(output: string) {
  const lines = output.trim().split('\n').slice(1)
  return lines.filter((l) => l.trim()).map((line) => {
    const parts = line.trim().split(/\s+/)
    const caption = parts[0]
    const free = parseInt(parts[1]) || 0
    const total = parseInt(parts[2]) || 0
    const used = total - free
    return {
      filesystem: caption,
      mount: caption,
      total,
      used,
      available: free,
      usagePercent: total > 0 ? Math.round((used / total) * 100) : 0,
    }
  })
}

async function networkInfo() {
  const interfaces = os.networkInterfaces()
  const result: Record<string, any[]> = {}
  for (const [name, addrs] of Object.entries(interfaces)) {
    if (!addrs) continue
    result[name] = addrs.map((a) => ({
      address: a.address,
      family: a.family,
      internal: a.internal,
      mac: a.mac,
    }))
  }

  let listeningPorts: number[] = []
  try {
    if (os.platform() !== 'win32') {
      const output = execSync("lsof -iTCP -sTCP:LISTEN -P -n 2>/dev/null | awk 'NR>1{print $9}' | grep -oE '[0-9]+$' | sort -un", { encoding: 'utf-8' })
      listeningPorts = output.trim().split('\n').filter(Boolean).map(Number)
    }
  } catch { /* 忽略错误 */ }

  return {
    interfaces: result,
    hostname: os.hostname(),
    listeningPorts,
  }
}
